import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  TranscribeAndExtractResponse,
  VisitFullResponse,
} from '@yui-receipt/shared'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'
import { useAuth } from '@/store/auth'
import Recorder from '@/components/voice/Recorder'
import ProgressionNoteForm from '@/components/voice/ProgressionNoteForm'
import { uploadAudio } from '@/lib/voicesoap/uploader'
import { formatMmSs } from '@/lib/voicesoap/duration'

type VoiceStage = 'idle' | 'uploading' | 'transcribing' | 'done' | 'error'

export default function VisitRecordPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()
  const [voiceStage, setVoiceStage] = useState<VoiceStage>('idle')
  const [progress, setProgress] = useState(0)
  const [errMsg, setErrMsg] = useState('')
  const [showRecorder, setShowRecorder] = useState(false)

  const { data, isLoading } = useQuery<VisitFullResponse>({
    queryKey: ['visit', id],
    queryFn: () => api(`/visits/${id}`),
    enabled: !!id,
  })

  const visit = data?.visit ?? null
  const hasAudio = !!data?.audio

  async function handleVoiceComplete(r: {
    blob: Blob
    mimeType: string
    durationSec: number
    startedAt: number
  }) {
    if (!id) return
    setVoiceStage('uploading')
    setErrMsg('')
    try {
      const up = await uploadAudio({
        visitId: Number(id),
        blob: r.blob,
        mimeType: r.mimeType,
        onProgress: (p) => setProgress(p.ratio),
      })
      setVoiceStage('transcribing')
      const ext = await api<TranscribeAndExtractResponse>(
        `/visits/${id}/transcribe-and-extract`,
        {
          method: 'POST',
          body: { audioId: up.audio.id },
        },
      )
      void ext
      setVoiceStage('done')
      toastSuccess('ถอดเสียง+สร้างโน้ตสำเร็จ')
      navigate(`/visits/${id}/review`, { replace: true })
    } catch (err) {
      setVoiceStage('error')
      const msg = err instanceof ApiClientError ? err.message : 'ดำเนินการไม่สำเร็จ'
      setErrMsg(msg)
      toastError(msg)
    }
  }

  if (isLoading) {
    return <p className="max-w-3xl mx-auto p-6 text-slate-500">กำลังโหลด...</p>
  }
  if (!visit) {
    return (
      <p className="max-w-3xl mx-auto p-6 text-rose-600">
        ไม่พบการรักษา —{' '}
        <Link to="/visits" className="underline">
          กลับ
        </Link>
      </p>
    )
  }

  const isInitial = visit.visitType === 'initial'

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {isInitial ? 'บันทึกการรักษา (ครั้งแรก)' : 'Progression Note'} — {visit.patientNameSnapshot}
          </h1>
          <p className="text-xs text-slate-500">
            HN {visit.patientHnSnapshot} • {visit.visitDate} • {isInitial ? 'ครั้งแรก' : 'ติดตามผล'}
          </p>
        </div>
        <Link to="/visits" className="text-sm text-slate-500 hover:text-brand-600">
          ← กลับ
        </Link>
      </header>

      {/* ---- Form first ---- */}
      <div className="card">
        <ProgressionNoteForm
          visitId={Number(id)}
          existing={data?.progressionNote ?? null}
          patientInfo={data?.patient ? {
            nameTh: data.patient.nameTh,
            hn: data.patient.hn,
            dob: data.patient.dob,
            heightCm: data.patient.heightCm,
            weightKg: data.patient.weightKg,
            sex: data.patient.sex,
          } : null}
          therapistName={user?.username ?? null}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['visit', id] })
            navigate('/visits', { replace: true })
          }}
        />
      </div>

      {/* ---- Voice recorder button + collapsible section ---- */}
      <div className="card space-y-3">
        <button
          type="button"
          className={
            'w-full min-h-tap rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition ' +
            (showRecorder
              ? 'bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100'
              : 'bg-brand-50 border border-brand-200 text-brand-700 hover:bg-brand-100')
          }
          onClick={() => setShowRecorder(!showRecorder)}
        >
          {showRecorder ? '✕ ปิดอัดเสียง' : '🎙️ อัดเสียงช่วยกรอก'}
        </button>

        {showRecorder && (
          <div className="space-y-3">
            {hasAudio && voiceStage === 'idle' && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                การรักษานี้มีไฟล์เสียงแล้ว — อัดใหม่จะเขียนทับ
              </div>
            )}

            <Recorder
              onComplete={handleVoiceComplete}
              disabled={voiceStage === 'uploading' || voiceStage === 'transcribing'}
            />

            {voiceStage === 'uploading' && (
              <div className="space-y-1" role="status" aria-live="polite">
                <div className="text-sm text-slate-700">
                  กำลังอัปโหลด... {Math.round(progress * 100)}%
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-600 transition-[width]"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
              </div>
            )}
            {voiceStage === 'transcribing' && (
              <p className="text-sm text-slate-700" role="status" aria-live="polite">
                กำลังถอดเสียงและสร้างโน้ต... (อาจใช้เวลา 1-2 นาที)
              </p>
            )}
            {voiceStage === 'error' && (
              <div
                role="alert"
                className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
              >
                {errMsg || 'เกิดข้อผิดพลาด — ลองใหม่ได้'}
              </div>
            )}

            <p className="text-xs text-slate-500">
              เคล็ดลับ: อัดในที่เงียบ, ถือโทรศัพท์ห่างจากปากประมาณ 30 ซม, พูดชัดเจน — ระยะเวลาสูงสุด {formatMmSs(30 * 60)}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
